import { Box, Stack, Typography, useTheme } from "@mui/material";
import { X } from "lucide-react";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioItemIcon } from "web_myplann/Portfolio/Components/PortfolioItemIcon";
import { useLinkableItemSearch } from "web_myplann/Portfolio/Hooks/useLinkableItemSearch";
import type { LinkableItemOption } from "web_myplann/Portfolio/Types/linkableItemOption";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { SearchInput } from "web_myplann/Shared/Components/SearchInput";

const R = Resources.Portfolio.List.DocumentUpload.ContractPicker;

type Props = {
	open: boolean;
	onClose: () => void;
	options: LinkableItemOption[];
	selectedKey: string | null;
	onSelect: (option: LinkableItemOption | null) => void;
};

export const ContractPickerDrawer: FC<Props> = ({
	open,
	onClose,
	options,
	selectedKey,
	onSelect,
}) => {
	const { palette, iconSize, spacingTokens } = useTheme();
	const { t } = useResource();
	const { search, setSearch, filteredOptions } = useLinkableItemSearch(options);

	return (
		<MyPlannDrawer open={open} onClose={onClose} title={t(R.title)} grabber>
			<Box px={spacingTokens.lg} pb={spacingTokens.md} flexShrink={0}>
				<SearchInput
					placeholder={t(R.searchPlaceholder)}
					value={search}
					onChange={setSearch}
				/>
			</Box>

			<Stack flex="1 1 auto" minHeight={0} overflow="auto">
				<ListRow
					icon={
						<X
							size={iconSize.md}
							color={
								selectedKey === null ? palette.blue[600] : palette.grey[500]
							}
						/>
					}
					iconBg={palette.grey[100]}
					title={t(R.none)}
					pickerSelected={selectedKey === null}
					onClick={() => onSelect(null)}
				/>

				{filteredOptions.map((option, index) => (
					<ListRow
						key={option.key}
						icon={
							option.item.kind === PortfolioItemKind.Property ? (
								<PortfolioItemIcon propertyType={option.item.data.property} />
							) : (
								<PortfolioItemIcon
									domain={option.item.data.domain}
									logo={option.item.data.icon}
								/>
							)
						}
						title={option.title}
						subtitle={option.subtitle}
						pickerSelected={option.key === selectedKey}
						isLast={index === filteredOptions.length - 1}
						onClick={() => onSelect(option)}
					/>
				))}

				{filteredOptions.length === 0 && (
					<Typography
						variant="body2"
						color={palette.grey[400]}
						textAlign="center"
						px={spacingTokens.lg}
						py={spacingTokens.xxl}
					>
						{t(R.noResults)}
					</Typography>
				)}
			</Stack>
		</MyPlannDrawer>
	);
};
